import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Clock, Users, TrendingUp } from "lucide-react";
import AppCard from "@/components/AppCard";
import { apps } from "@/data/apps";

const HomePage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
  };

  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: { staggerChildren: 0.1 }
    }
  };

  const values = [
    { icon: <Clock className="w-6 h-6" />, title: "Save Time", description: "AI that works for you, not against you." },
    { icon: <Users className="w-6 h-6" />, title: "Strengthen Families", description: "Tools designed for real life." },
    { icon: <TrendingUp className="w-6 h-6" />, title: "Increase Productivity", description: "Do more with less effort." },
    { icon: <Sparkles className="w-6 h-6" />, title: "Deepen Growth", description: "Personal and spiritual development." }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center pt-20" data-testid="hero-section">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="heading-hero mb-6" data-testid="hero-title">
              AI-powered apps for
              <br />
              <span className="bg-gradient-to-r from-purple-600 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">everyday life.</span>
            </h1>
            <p className="body-lg max-w-2xl mx-auto mb-10">
              Practical tools that solve real problems. From learning to eating healthy, 
              from spiritual growth to creative work.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/apps" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="hero-explore-btn">
                Explore Apps
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/about" className="btn-secondary inline-flex items-center justify-center" data-testid="hero-about-btn">
                Learn More
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Apps Grid Section */}
      <section className="section-spacing bg-white" data-testid="apps-grid-section">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <p className="caption mb-4">Our Apps</p>
            <h2 className="heading-2 mb-4">Built with purpose.</h2>
            <p className="body-text max-w-xl mx-auto">
              Each app is designed to make your life better in a meaningful way.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="bento-grid"
          >
            {apps.map((app, index) => (
              <Link to="/apps" key={app.id}>
                <AppCard app={app} index={index} />
              </Link>
            ))}
          </motion.div>

          <motion.div {...fadeInUp} className="text-center mt-12">
            <Link to="/apps" className="btn-link inline-flex items-center gap-2" data-testid="view-all-apps-link">
              View all apps
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-spacing bg-[#F5F5F7]" data-testid="philosophy-section">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <p className="caption mb-4">Our Philosophy</p>
            <h2 className="heading-2 mb-4">Technology with purpose.</h2>
            <p className="body-text max-w-2xl mx-auto">
              We believe technology should help you live better, not just consume more. 
              Every app we build serves a real need.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                variants={fadeInUp}
                className="feature-card text-center"
              >
                <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-4 text-zinc-600">
                  {value.icon}
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">{value.title}</h3>
                <p className="text-sm text-zinc-500">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-zinc-900" data-testid="cta-section">
        <div className="container-main text-center">
          <motion.div {...fadeInUp}>
            <h2 className="heading-2 text-white mb-4">Ready to get started?</h2>
            <p className="text-lg text-zinc-400 mb-8 max-w-xl mx-auto">
              Explore our apps and find the perfect tool to help you achieve your goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/apps"
                className="h-12 px-8 rounded-full bg-white text-zinc-900 font-medium text-sm hover:bg-zinc-100 transition-all inline-flex items-center justify-center gap-2"
                data-testid="cta-explore-btn"
              >
                Explore Apps
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="h-12 px-8 rounded-full border border-zinc-700 text-white font-medium text-sm hover:bg-zinc-800 transition-all inline-flex items-center justify-center"
                data-testid="cta-contact-btn"
              >
                Get in Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
